import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, TextField, Grid, CircularProgress} from 'views/components';
import {LocationOn} from 'views/icons';
import {Map, Translate, translate} from 'views/module';

const Location = (props) => {
  const {newJob, updateJobLocation, setIsStepValid} = props;

  const location = newJob.location || {};
  const neighborhood = location.neighborhood || '';
  const city = location.city || '';
  const geoCode = location.geoCode || {latitude: null, longitude: null};

  const [isLoadingMap, setIsLoadingMap] = React.useState(false);

  // Update step validity when location data changes
  React.useEffect(() => {
    const isValid = neighborhood.trim() !== '' && city.trim() !== '' && geoCode.latitude != null && geoCode.longitude != null;
    setIsStepValid(isValid);
  }, [neighborhood, city, geoCode, setIsStepValid]);

  const handleNeighborhoodChange = (event) => {
    const value = event;
    updateJobLocation((location) => ({
      ...location,
      neighborhood: value,
    }));
  };

  const handleCityChange = (event) => {
    const value = event;
    updateJobLocation((location) => ({
      ...location,
      city: value,
    }));
  };

  const handleGeoCodeUpdate = (event) => {
    const value = event;
    updateJobLocation((location) => ({
      ...location,
      geoCode: {
        latitude: Number.isFinite(parseFloat(value.lat)) ? parseFloat(value.lat) : null,
        longitude: Number.isFinite(parseFloat(value.lon)) ? parseFloat(value.lon) : null,
      },
    }));
  };

  // Helper to build location string
  const locationString = React.useMemo(() => {
    const neighborhoodText = neighborhood.trim();
    const cityText = city.trim();
    if (neighborhoodText && cityText) {
      return `${neighborhoodText}, ${cityText}, Canada`;
    } else if (neighborhoodText) {
      return `${neighborhoodText}, Canada`;
    } else if (cityText) {
      return `${cityText}, Canada`;
    } else {
      return '';
    }
  }, [neighborhood, city]);

  // Geocode function
  const geocodeLocation = async () => {
    if (neighborhood.trim() === '' || city.trim() === '') {
      handleGeoCodeUpdate({lat: null, lon: null});
      setIsLoadingMap(false);
      return;
    }
    setIsLoadingMap(true);
    try {
      const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(locationString)}`,
      );
      const data = await res.json();
      if (data.length > 0) {
        handleGeoCodeUpdate(data[0]);
      } else {
        handleGeoCodeUpdate({lat: null, lon: null});
      }
    } catch (err) {
      handleGeoCodeUpdate({lat: null, lon: null});
    } finally {
      setIsLoadingMap(false);
    }
  };

  // Debounce geocoding while typing
  React.useEffect(() => {
    const t = setTimeout(() => {
      geocodeLocation();
    }, 800);
    return () => clearTimeout(t);
  }, [locationString]);

  return (
    <Box sx={{p: 2, maxWidth: 1200, mx: 'auto'}}>
      <Typography variant="h4" gutterBottom sx={{textAlign: 'center', mb: 4}}>
        <Translate expression="career.create.Location.Title" />
      </Typography>

      {/* Form Fields */}
      <Box sx={{maxWidth: 900, mx: 'auto', mb: 4}}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 500}}>
              <Translate expression="career.create.Location.NeighborhoodLabel" />
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
              <Translate expression="career.create.Location.NeighborhoodDescription" />
            </Typography>
            <TextField
              fullWidth
              value={neighborhood}
              onChange={handleNeighborhoodChange}
              placeholder={translate('career.create.Location.NeighborhoodPlaceholder')}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 500}}>
              <Translate expression="career.create.Location.CityLabel" />
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
              <Translate expression="career.create.Location.CityDescription" />
            </Typography>
            <TextField
              fullWidth
              value={city}
              onChange={handleCityChange}
              placeholder={translate('career.create.Location.CityPlaceholder')}
              variant="outlined"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Location Preview and Map */}
      <Box sx={{maxWidth: 900, mx: 'auto'}}>
        {
          isLoadingMap && (
            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300}}>
              <CircularProgress size={48} color="primary" />
            </Box>
          )
        }
        {
          !isLoadingMap && geoCode && geoCode.latitude != null && geoCode.longitude != null && (
            <Box sx={{height: 300, width: '100%', borderRadius: 2, overflow: 'hidden', border: '1px solid #e3f2fd'}}>
              <Map
                key={(geoCode.latitude + ',' + geoCode.longitude)}
                height="100%"
                width="100%"
                coordinates={[geoCode.latitude, geoCode.longitude]}
              />
            </Box>
          )
        }
        {
          !isLoadingMap && (!geoCode || geoCode.latitude == null || geoCode.longitude == null) && (
            <Box
              sx={{
                p: 3,
                backgroundColor: '#f0f7ff',
                borderRadius: 2,
                border: '1px solid #e3f2fd',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: 300,
                gap: 1,
              }}
            >
              <LocationOn sx={{color: '#1976d2', fontSize: 48}} />
              <Typography variant="subtitle2" sx={{fontWeight: 600, color: '#1976d2', mb: 0.5, textAlign: 'center'}}>
                <Translate expression="career.create.Location.LocationPreviewTitle" />
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center'}}>
                <Translate expression="career.create.Location.LocationPreviewNoLocation" />
              </Typography>
            </Box>
          )
        }
      </Box>
    </Box>
  );
};

Location.propTypes = {
  newJob: PropTypes.object.isRequired,
  updateJobLocation: PropTypes.func.isRequired,
  setIsStepValid: PropTypes.func.isRequired,
};

export default Location;
